import React from 'react';

import type { UniqueIdentifier, ScreenReaderInstructions, Announcements } from '@lumapps/lumx-dnd/types';
import { useTranslate } from '@lumapps/translations';

import { SortableListProps } from '../components/SortableList/types';
import { LUMX_LISTS } from '../keys';

export interface UseSortableAccessibilityInstructionsReturn {
    screenReaderInstructions: ScreenReaderInstructions;
    announcements: Announcements;
}

export interface UseSortableAccessibilityInstructionsParams<T> {
    /**
     * Reference to the container element of the sortable list. Used to compute items per row for announcements.
     */
    containerRef: React.RefObject<HTMLDivElement>;
    mode: SortableListProps<T>['mode'];
    entities: T[];
    active: unknown | null;
    /**
     * Method to return the index of an entity in the sortable context
     */
    getEntityIndex: (id: UniqueIdentifier) => number;
}

export const useSortableAccessibilityInstructions = <T,>({
    containerRef,
    mode,
    entities,
    active,
    getEntityIndex,
}: UseSortableAccessibilityInstructionsParams<T>): UseSortableAccessibilityInstructionsReturn => {
    const { translateKey, translateAndReplace } = useTranslate();

    const isFirstAnnouncement = React.useRef(true);
    const accessibility: UseSortableAccessibilityInstructionsReturn = React.useMemo(() => {
        const getPosition = (id: UniqueIdentifier) => getEntityIndex(id) + 1;
        /**
         * Count actual items per row, since the columns props is optional and the list can be responsive.
         */
        function countItemsPerRow(): number {
            const items = Array.from(containerRef.current?.children || []) as HTMLElement[];
            if (items.length === 0) {
                return 0;
            }

            const firstRowTop = items[0].offsetTop;
            return items.filter((item) => item.offsetTop === firstRowTop).length;
        }
        /**
         * Count actual rows, since the list can be responsive.
         */
        function countRows(): number {
            const items = Array.from(containerRef.current?.children || []) as HTMLElement[];
            if (items.length === 0) {
                return 0;
            }

            let rowCount = 0;
            let lastTop = -1;
            items.forEach((item) => {
                if (item.offsetTop !== lastTop) {
                    rowCount += 1;
                    lastTop = item.offsetTop;
                }
            });
            return rowCount;
        }
        /**
         * Get the row and column position of an item based on its id.
         */
        const getRowPosition = (id: UniqueIdentifier) => {
            const columnsCount = countItemsPerRow();
            const index = getEntityIndex(id);
            return {
                row: Math.floor(index / columnsCount) + 1,
                column: (index % columnsCount) + 1,
            };
        };

        return {
            screenReaderInstructions: {
                draggable: translateKey(LUMX_LISTS.SORTABLE_SCREEN_READER_INSTRUCTIONS),
            },
            announcements: {
                onDragStart({ active: { id } }) {
                    return translateAndReplace(LUMX_LISTS.DRAG_STARTING, {
                        START_POSITION: getPosition(id),
                    });
                },
                onDragOver({ active, over }) {
                    // In this specific use-case, the picked up item's `id` is always the same as the first `over` id.
                    // The first `onDragOver` event therefore doesn't need to be announced, because it is called
                    // immediately after the `onDragStart` announcement and is redundant.
                    if (isFirstAnnouncement.current === true) {
                        isFirstAnnouncement.current = false;
                        return undefined;
                    }

                    if (over) {
                        // Check dynamically if the list has multiple rows (since responsive can change that).
                        const hasRows =
                            mode === 'grid' ||
                            (mode === 'horizontal' && countRows() > 1) ||
                            (mode === 'vertical' && countItemsPerRow() > 1);

                        if (hasRows) {
                            const activeRowPosition = getRowPosition(active.id);
                            const overRowPosition = getRowPosition(over.id);
                            return translateAndReplace(LUMX_LISTS.DRAG_UPDATES_WITH_ROWS, {
                                START_ROW_POSITION: activeRowPosition?.column,
                                START_ROW: activeRowPosition?.row,
                                END_ROW_POSITION: overRowPosition?.column,
                                END_ROW: overRowPosition?.row,
                                NEW_POSITION: getPosition(over.id),
                                TOTAL_ITEMS: entities.length,
                            });
                        }
                        return translateAndReplace(LUMX_LISTS.DRAG_UPDATES, {
                            START_POSITION: getPosition(active.id),
                            END_POSITION: getPosition(over.id),
                        });
                    }

                    return undefined;
                },
                onDragEnd({ active, over }) {
                    if (over) {
                        return translateAndReplace(LUMX_LISTS.DRAG_ENDED, {
                            START_POSITION: getPosition(active.id),
                            END_POSITION: getPosition(over.id),
                        });
                    }
                    return undefined;
                },
                onDragCancel({ active: { id } }) {
                    return translateAndReplace(LUMX_LISTS.DRAG_CANCELED, {
                        START_POSITION: getPosition(id),
                    });
                },
            },
        };
    }, [containerRef, entities.length, getEntityIndex, mode, translateAndReplace, translateKey]);

    React.useEffect(() => {
        if (active == null) {
            isFirstAnnouncement.current = true;
        }
    }, [active]);

    return accessibility;
};
